import { useEffect, useRef, useState, type ChangeEvent } from 'react'
import {
  formatAttachmentSize,
  uploadAttachment,
  type MessagingAttachmentSource,
} from '../../../api/attachments'
import { PexIcon } from '../../../components/icons/PexIcon'
import { useT } from '../../../i18n'
import { messagingI18n } from '../../../i18n/messaging'
type MessagingAttachmentPickerProps = {
  appearance?: 'default' | 'messenger'
  source: MessagingAttachmentSource
  selectedIds: string[]
  onSelectionChange: (ids: string[]) => void
  disabled: boolean
}

export function MessagingAttachmentPicker({
  appearance = 'default',
  source,
  selectedIds,
  onSelectionChange,
  disabled,
}: MessagingAttachmentPickerProps) {
  const t = useT(messagingI18n)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadError, setUploadError] = useState(false)
  const mountedRef = useRef(true)
  const component = source.component

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  if (!component) {
    return (
      <p
        className="rounded-[var(--radius-panel)] border border-[var(--color-border)] bg-[var(--color-surface-muted)] px-3 py-2 text-xs text-[var(--color-text-muted)]"
        role="note"
      >
        {t.attachments.sampleUnavailable}
      </p>
    )
  }

  const toggle = (id: string) => {
    onSelectionChange(
      selectedIds.includes(id)
        ? selectedIds.filter((selectedId) => selectedId !== id)
        : [...selectedIds, id],
    )
  }

  const upload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file || disabled || isUploading) return

    setUploadError(false)
    setIsUploading(true)
    try {
      const created = await uploadAttachment(
        component,
        source.objectId,
        file,
      )
      if (!mountedRef.current) return
      onSelectionChange([...new Set([...selectedIds, created.id])])
      await source.refetch()
    } catch {
      if (mountedRef.current) setUploadError(true)
    } finally {
      event.target.value = ''
      if (mountedRef.current) setIsUploading(false)
    }
  }

  return (
    <section
      aria-label={t.attachments.title}
      data-appearance={appearance}
      className={
        appearance === 'messenger'
          ? 'rounded-xl border border-white/10 bg-[#171c23] p-2.5'
          : 'rounded-[var(--radius-panel)] border border-[var(--color-border)] bg-[var(--color-surface-muted)] p-2.5'
      }
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className={appearance === 'messenger' ? 'text-xs font-semibold text-slate-100' : 'text-xs font-semibold text-[var(--color-text)]'}>
          {t.attachments.title}
        </p>
        <button
          type="button"
          disabled={disabled || isUploading}
          onClick={() => fileInputRef.current?.click()}
          className={
            appearance === 'messenger'
              ? 'inline-flex min-h-11 items-center gap-2 rounded-lg border border-white/10 bg-white/[0.06] px-3 text-xs font-semibold text-slate-100 transition hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus)] disabled:cursor-not-allowed disabled:opacity-50'
              : 'inline-flex min-h-11 items-center gap-2 rounded-[var(--list-control-radius)] border border-[var(--list-control-border)] bg-[var(--list-control-bg)] px-3 text-xs font-semibold text-[var(--color-text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus)] disabled:cursor-not-allowed disabled:opacity-50'
          }
        >
          <PexIcon
            name={isUploading ? 'refresh' : 'upload'}
            size="sm"
            decorative
            className={isUploading ? 'animate-spin motion-reduce:animate-none' : ''}
          />
          {isUploading ? t.attachments.uploading : t.attachments.addFile}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          className="sr-only"
          aria-label={t.attachments.uploadLabel}
          disabled={disabled || isUploading}
          onChange={(event) => void upload(event)}
        />
      </div>

      {isUploading ? (
        <p className="mt-2 text-xs text-[var(--color-text-muted)]" role="status">
          {t.attachments.uploading}
        </p>
      ) : null}
      {uploadError ? (
        <p className="mt-2 text-xs font-medium text-[var(--color-danger)]" role="alert">
          {t.attachments.uploadFailed}
        </p>
      ) : null}
      {source.isLoading ? (
        <p className="mt-2 text-xs text-[var(--color-text-muted)]" role="status">
          {t.attachments.loading}
        </p>
      ) : null}
      {source.isError ? (
        <div className="mt-2 flex items-center justify-between gap-2" role="alert">
          <p className="text-xs text-[var(--color-danger)]">
            {t.attachments.loadFailed}
          </p>
          <button
            type="button"
            className="min-h-11 rounded-[var(--list-control-radius)] border border-[var(--list-control-border)] px-3 text-xs font-semibold"
            onClick={() => void source.refetch()}
          >
            {t.attachments.retry}
          </button>
        </div>
      ) : null}
      {!source.isLoading && !source.isError && source.rows.length === 0 ? (
        <p className="mt-2 text-xs text-[var(--color-text-muted)]">
          {t.attachments.empty}
        </p>
      ) : null}
      {source.rows.length > 0 ? (
        <ul className={`mt-2 grid gap-1.5 ${appearance === 'default' ? 'sm:grid-cols-2' : ''}`}>
          {source.rows.map((row) => (
            <li key={row.id}>
              <label
                className={
                  appearance === 'messenger'
                    ? 'flex min-h-11 cursor-pointer items-center gap-2 rounded-lg border border-white/10 bg-white/[0.04] px-2.5 py-2 text-xs text-slate-100 transition hover:bg-white/[0.08] focus-within:ring-2 focus-within:ring-[var(--color-focus)]'
                    : 'flex min-h-11 cursor-pointer items-center gap-2 rounded-[var(--list-control-radius)] border border-[var(--color-border)] bg-[var(--color-surface)] px-2.5 py-2 text-xs text-[var(--color-text)] focus-within:ring-2 focus-within:ring-[var(--color-focus)]'
                }
              >
                <input
                  type="checkbox"
                  checked={selectedIds.includes(row.id)}
                  disabled={disabled || isUploading}
                  onChange={() => toggle(row.id)}
                  className="h-4 w-4 accent-[var(--color-action)]"
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium">
                    {row.original_filename}
                  </span>
                  <span className="block text-[11px] text-[var(--color-text-muted)]">
                    {formatAttachmentSize(row.file_size)}
                  </span>
                </span>
              </label>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  )
}
